import React, { useEffect, useState } from "react";
import { Table, Modal, Input, InputNumber, Switch, Button } from "antd";
import toast from "react-hot-toast";
import "./AdminPackages.css";
import "../../App.css";
import { formatCurrency } from "../../config/currency";

const { TextArea } = Input;

const emptyForm = {
  id: null,
  title: "",
  price: 0,
  duration: "",
  inclusions: "",
  is_active: true,
  sort_order: 0,
};

function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadPackages = () => {
    setLoading(true);
    const url =
      window.API_PREFIX +
      "booking/admin/packages?Token=" +
      localStorage.getItem("DToken");
    fetch(url)
      .then((resp) => resp.json())
      .then((data) => {
        if (data.status === "1") {
          setPackages(data.packages || []);
        } else {
          toast.error("Failed to load packages");
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load packages");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setForm({
      id: record.id,
      title: record.title,
      price: record.price,
      duration: record.duration,
      inclusions: (record.inclusions || []).join("\n"),
      is_active: record.is_active,
      sort_order: record.sort_order,
    });
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const isEdit = Boolean(form.id);
    const url =
      window.API_PREFIX +
      (isEdit ? "booking/admin/update_package" : "booking/admin/add_package");
    const payload = {
      Token: localStorage.getItem("DToken"),
      title: form.title.trim(),
      price: form.price,
      duration: form.duration.trim(),
      inclusions: form.inclusions
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
      is_active: form.is_active,
      sort_order: form.sort_order,
    };
    if (isEdit) payload.id = form.id;

    fetch(url, {
      method: "POST",
      headers: { "Content-type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data.status === "1") {
          toast.success(isEdit ? "Package updated" : "Package created");
          setModalOpen(false);
          loadPackages();
        } else {
          toast.error(data.message || "Save failed");
        }
        setSaving(false);
      })
      .catch(() => {
        toast.error("Save failed");
        setSaving(false);
      });
  };

  const handleDelete = (record) => {
    if (!window.confirm(`Delete package "${record.title}"?`)) return;
    const url = window.API_PREFIX + "booking/admin/delete_package";
    fetch(url, {
      method: "POST",
      headers: { "Content-type": "application/json" },
      body: JSON.stringify({
        Token: localStorage.getItem("DToken"),
        id: record.id,
      }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data.status === "1") {
          toast.success("Package deleted");
          loadPackages();
        } else {
          toast.error("Delete failed");
        }
      });
  };

  const columns = [
    {
      title: "Sr No.",
      render: (_, __, index) => index + 1,
      width: 70,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Price (AED)",
      dataIndex: "price",
      key: "price",
      width: 110,
      render: (price) => formatCurrency(price),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      width: 120,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 90,
      render: (active) => (active ? "Yes" : "No"),
    },
    {
      title: "Sort",
      dataIndex: "sort_order",
      key: "sort_order",
      width: 80,
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <div className="admin_packages_actions">
          <Button type="link" onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="container-fuild mx-2">
      <div className="admin_packages_toolbar">
        <h2 className="admin_packages_title">Packages</h2>
        <Button type="primary" className="admin_packages_add_btn" onClick={openCreate}>
          Add Package
        </Button>
      </div>
      <div className="admin_packages_table_wrap">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={packages}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </div>

      <Modal
        title={form.id ? "Edit Package" : "Add Package"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={560}
      >
        <div className="admin_packages_form">
          <label>Title *</label>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. OPD Consultation"
          />
          <label>Price (AED)</label>
          <InputNumber
            className="w-100"
            min={0}
            value={form.price}
            onChange={(val) => setForm({ ...form, price: val || 0 })}
          />
          <label>Duration</label>
          <Input
            value={form.duration}
            onChange={(e) => setForm({ ...form, duration: e.target.value })}
            placeholder="e.g. 15min"
          />
          <label>What&apos;s included (one per line)</label>
          <TextArea
            rows={4}
            value={form.inclusions}
            onChange={(e) => setForm({ ...form, inclusions: e.target.value })}
            placeholder="Consultation&#10;Follow-up call"
          />
          <label>Sort order</label>
          <InputNumber
            className="w-100"
            min={0}
            value={form.sort_order}
            onChange={(val) => setForm({ ...form, sort_order: val || 0 })}
          />
          <div className="admin_packages_active_row">
            <span>Active</span>
            <Switch
              checked={form.is_active}
              onChange={(checked) => setForm({ ...form, is_active: checked })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AdminPackages;
